// Staff at a location as multi-origin config (ADR-015; phase 3A, register on synced config; lane D4 employees): the
// employee record the Back Office edits (legacy `employees`: names, contact, job role, wage, external id, standing, POS
// permissions) and what the register needs to sign someone in offline (grants and the PIN verifier). A manager edits a
// staff member at the node or in the cloud; the write is one `ConfigFieldsWritten` fact merged per field by HLC on every
// replica, and each register at the location receives the member's effective fields minus their contact details as a
// config delta.
//
// The PIN itself never travels: the command takes it once, and the server records only the device verifier,
// sha256(`radish-pin:` + employee id + `:` + PIN) as lowercase hex (register plan §1 rule 4). Honest limit (target model
// §4.4): a 4-digit PIN has 10^4 values, so a device that holds a verifier can try them all; the protection is device
// custody, lockout and short sessions, not the hash.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { secret } from '../../define/secret.ts';
import { EmployeeId, JobRoleId, LocalDate, LocationId, Money, TimecardId } from '../../primitives/codecs.ts';
import { CatalogEntityNotFound, CatalogInvalid } from '../catalog/index.ts';
import { CloudAuthoritative, ConfigFieldsWritten, ProposalRequired } from '../config/index.ts';
import { EmployeeNotFound, LocationTime, PinTaken, TimecardMealPremiumDay } from './index.ts';

/**
 * The legacy POS permission keys (legacy `PermissionRegistry`, 20 keys, parity employees-roles-permissions §3.4.1), in
 * the registry's order, plus the three keys the rebuild added (force-close, manage employees, Training mode). Core's commerce, cash and
 * labor machines and the register's prompts check them by these names.
 */
export const StaffGrant = z
  .enum([
    'cash_drawer', 'open_and_close_drawer_session', 'cash_management', 'server_cash_banks_enabled', 'server_cash_banks_exclusive', 'create_paidout',
    'void_transaction', 'create_discount', 'transfer_order', 'change_tax_profiles',
    'manage_settings', 'manage_online_store_settings', 'view_reports', 'item_management', 'restrict_user_to_item_availability_only', 'exit_app',
    'manage_shift_reviews', 'approve_open_shift_claims', 'approve_early_breaks', 'table_assignment',
    'orders.force_complete', 'manage_employees',
    // Register Training mode (IMPROVE L716; permission register.training.use).
    'training_mode',
  ])
  .meta({ id: 'StaffGrant' });

/**
 * What each grant does to its holder (lane RG, B606): most allow something, a few restrict (a server who may only use a
 * bank, a user limited to item availability). A register on an older build drops a grant it does not know, which is safe
 * for an allowing grant (the holder just lacks it) and unsafe for a restricting one, so a restricting grant is named
 * `restrict_*`, `*_exclusive` or `*_only`, and a register refuses the staff row when an unknown grant is named that way.
 * The Record type makes every new grant be classified here; `staff-grant-effects.test.ts` holds the naming.
 */
export const STAFF_GRANT_EFFECT: Readonly<Record<z.output<typeof StaffGrant>, 'allows' | 'restricts'>> = {
  cash_drawer: 'allows', open_and_close_drawer_session: 'allows', cash_management: 'allows', server_cash_banks_enabled: 'allows',
  server_cash_banks_exclusive: 'restricts', create_paidout: 'allows', void_transaction: 'allows', create_discount: 'allows', transfer_order: 'allows',
  change_tax_profiles: 'allows', manage_settings: 'allows', manage_online_store_settings: 'allows', view_reports: 'allows', item_management: 'allows',
  restrict_user_to_item_availability_only: 'restricts', exit_app: 'allows', manage_shift_reviews: 'allows', approve_open_shift_claims: 'allows',
  approve_early_breaks: 'allows', table_assignment: 'allows', 'orders.force_complete': 'allows', manage_employees: 'allows', training_mode: 'allows',
};

/** Whether a grant's name says it restricts (the convention `STAFF_GRANT_EFFECT` holds every grant to). */
export const restrictiveGrantName = (grant: string): boolean => /^restrict_|_exclusive$|_only$/.test(grant);

/** The grants a new employee starts with (legacy registry defaults: the four keys whose default is true). */
export const DEFAULT_STAFF_GRANTS = ['open_and_close_drawer_session', 'change_tax_profiles', 'manage_online_store_settings', 'restrict_user_to_item_availability_only'] as const;

export const StaffRole = z.enum(['manager', 'bartender', 'server', 'barback', 'cook', 'host', 'other']).meta({ id: 'StaffRole' });

/** sha256(`radish-pin:` + employee id + `:` + PIN), lowercase hex. */
export const PinVerifier = z.string().regex(/^[0-9a-f]{64}$/).meta({ id: 'PinVerifier' });

const optionalText = z.string().trim().max(255).nullable();

/** Legacy caps an hourly wage at $200.00 (`wage` ≤ 20000 cents); the rebuild also refuses a negative one (FIX-EMP-3). */
export const HourlyWage = Money.refine((m) => m.minor >= 0n && m.minor <= 20_000n, { message: 'A wage is between $0.00 and $200.00 an hour' }).meta({
  id: 'HourlyWage',
});

/** Who to reach and where (legacy `email`, `phone`, `address`, `city`, `state`, `zip`). PII: never sent to a device. */
export const StaffContact = z
  .object({
    email: z.email().max(255).nullable(),
    phone: optionalText,
    address: optionalText,
    city: optionalText,
    state: optionalText,
    zip: optionalText,
  })
  .meta({ id: 'StaffContact' });

/** A meal-period waiver key (legacy `WaiverTypeRegistry`: First and Second Meal Period Waiver). */
export const MealWaiver = z.enum(['ca_meal_first', 'ca_meal_second']).meta({ id: 'MealWaiver' });

export const StaffMemberFields = z
  .object({
    /** The display name, "First Last" (legacy derived attribute `name`); kept in step with the two name fields. */
    name: z.string().trim().min(1).max(255),
    firstName: z.string().trim().min(1).max(255),
    lastName: optionalText,
    role: StaffRole,
    /** The employee's job role at the location (legacy `role_id`; one role per employee). */
    jobRoleId: JobRoleId.nullable(),
    grants: z.array(StaffGrant).max(30),
    /** null until a PIN is set; a member without one cannot sign in at the register. */
    pinVerifier: PinVerifier.nullable(),
    /** Digits in the PIN (4–8), so the register's pad knows when it is complete. */
    pinLength: z.int().min(4).max(8).nullable(),
    active: z.boolean(),
    /** Pay per hour (legacy `employees.wage`, cents). */
    wage: HourlyWage,
    /** The payroll provider's id for this employee (legacy `external_id`; leading zeros matter, never a number). */
    externalEmployeeId: optionalText,
    startOn: LocalDate.nullable(),
    endOn: LocalDate.nullable(),
    /** Crew access (legacy `portal_enabled`, default true, and `is_manager`). */
    portalEnabled: z.boolean(),
    isManager: z.boolean(),
    contact: StaffContact,
    /**
     * Meal-period waivers on file (legacy `employee_waivers`, keys `ca_meal_first` / `ca_meal_second`): a first-meal waiver
     * covers shifts of up to 6 worked hours, a second-meal waiver up to 12 (core `assessMealPremiums`).
     */
    mealWaivers: z.array(MealWaiver).max(2),
    /** Exempt from meal-break premiums (legacy `employee_meal_premium_exemptions`, an employer classification). */
    mealPremiumExempt: z.boolean(),
  })
  .meta({ id: 'StaffMemberFields' });

/** The fields a command may change (the PIN verifier, its length and the display name are derived). */
export const StaffMemberChanges = StaffMemberFields.omit({ pinVerifier: true, pinLength: true }).partial().meta({ id: 'StaffMemberChanges' });

export const StaffMemberView = StaffMemberFields.omit({ pinVerifier: true })
  .extend({ employeeId: EmployeeId, locationId: LocationId, hasPin: z.boolean(), version: z.int().nonnegative() })
  .meta({ id: 'StaffMemberView' });

/** Legacy `unique:employee_roles,name` per location among roles not deleted. */
export const JobRoleNameTaken = defineError({
  code: 'labor.job_role_name_taken', httpStatus: 422, message: 'errors.labor.job_role_name_taken', retryable: false,
  details: z.object({ name: z.string() }),
});
export const JobRoleNotFound = defineError({
  code: 'labor.job_role_not_found', httpStatus: 404, message: 'errors.labor.job_role_not_found', retryable: false,
  details: z.object({ jobRoleId: JobRoleId }),
});

export const UpsertStaffMember = defineCommand({
  name: 'labor.upsertStaffMember',
  summary: "Add an employee at a location or change their details, job role, wage, POS permissions, PIN or standing.",
  input: z.object({
    employeeId: EmployeeId,
    locationId: LocationId,
    changes: StaffMemberChanges,
    /** A new PIN (4–8 digits), a secret (D7). Only its device verifier is stored; unique at the location. */
    pin: secret(z.string().regex(/^\d{4,8}$/)).optional(),
  }),
  output: StaffMemberView,
  // v2 (lane D4) added the employee record: the name split, job role, wage, payroll id, dates, crew flags and contact.
  // A v1 result predates all of them, so each takes its new-employee default and the whole name stays the first name.
  // v3 (lane D4) added the meal-period waivers and the meal-premium exemption; an older result had neither.
  outputVersion: 3,
  outputUpcastFrom: [{
    version: 1,
    up: (old: unknown) => ({
      firstName: (old as { readonly name: string }).name, lastName: null, jobRoleId: null, wage: { minor: '0', currency: 'USD' }, externalEmployeeId: null,
      startOn: null, endOn: null, portalEnabled: true, isManager: false,
      contact: { email: null, phone: null, address: null, city: null, state: null, zip: null },
      ...(old as object),
    }),
  }, { version: 2, up: (old: unknown) => ({ mealWaivers: [], mealPremiumExempt: false, ...(old as object) }) }],
  // Legacy gates every employee screen and write on `manage_employees`.
  permission: 'labor.timecards.manage',
  risk: 'high',
  availability: 'location',
  origin: 'any',
  emits: [ConfigFieldsWritten],
  errors: [CatalogInvalid, CloudAuthoritative, ProposalRequired, CatalogEntityNotFound, PinTaken, JobRoleNotFound],
  dryRun: true,
  agent: { expose: false },
  legacy: ['POST /v1/employees', 'PUT /v1/employees/{id}'],
});

// ---- job roles -------------------------------------------------------------------------------------------------------
/**
 * A location's job role (legacy `employee_roles`): a name, a colour for the schedule, and a template wage and POS
 * permissions that the Back Office copies into an employee when the role is picked. Later role edits never change an
 * employee (legacy R-EROLE-1), and pay always uses the employee's own wage (R-EROLE-6).
 */
export const JobRoleFields = z
  .object({
    name: z.string().trim().min(1).max(255),
    description: optionalText,
    wage: HourlyWage,
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).nullable(),
    grants: z.array(StaffGrant).max(30),
    sortKey: z.int().min(0).max(100_000),
  })
  .meta({ id: 'JobRoleFields' });

export const JobRoleView = JobRoleFields.extend({
  jobRoleId: JobRoleId,
  locationId: LocationId,
  /** Employees who hold the role (legacy `employees_count`). */
  employeeCount: z.int().nonnegative(),
  version: z.int().nonnegative(),
}).meta({ id: 'JobRoleView' });

const roleAdmin = {
  availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], permission: 'settings.staff.manage', output: JobRoleView,
  risk: 'medium', dryRun: true, agent: { expose: false },
} as const;

export const UpsertJobRole = defineCommand({
  ...roleAdmin,
  name: 'labor.upsertJobRole',
  summary: 'Create a job role at a location, or change some of its fields (its order is `sortKey`).',
  input: z.object({ jobRoleId: JobRoleId, locationId: LocationId, changes: JobRoleFields.partial() }),
  errors: [JobRoleNameTaken, JobRoleNotFound, CatalogInvalid, CloudAuthoritative],
  legacy: ['POST /v1/employee-roles', 'PUT /v1/employee-roles/{id}', 'PUT /v1/employee-roles-reorder'],
});

export const DeleteJobRole = defineCommand({
  ...roleAdmin,
  name: 'labor.deleteJobRole',
  summary: 'Delete a job role. Its employees keep their wage and permissions and show no role (legacy nulls `role_id`).',
  input: z.object({ jobRoleId: JobRoleId, locationId: LocationId }),
  errors: [JobRoleNotFound, CloudAuthoritative],
  legacy: ['DELETE /v1/employee-roles/{id}'],
});

export const ListJobRoles = defineQuery({
  name: 'labor.listJobRoles',
  summary: "A location's job roles in their order (sort key, then name), each with how many employees hold it.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ jobRoles: z.array(JobRoleView) }),
  permission: 'labor.timecards.manage',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['GET /v1/employee-roles'],
});

// ---- the employee record ---------------------------------------------------------------------------------------------
/** Where an employee stands on the time clock now (legacy pane `clock_status` and `clock_status_since`). */
export const ClockStatus = z
  .object({ state: z.enum(['clocked_out', 'clocked_in', 'on_break']), since: LocationTime.nullable() })
  .meta({ id: 'ClockStatus' });

export const EmployeeRecord = StaffMemberFields.omit({ pinVerifier: true })
  .extend({
    employeeId: EmployeeId,
    locationId: LocationId,
    jobRoleName: z.string().nullable(),
    jobRoleColor: z.string().nullable(),
    hasPin: z.boolean(),
    version: z.int().nonnegative(),
  })
  .meta({ id: 'EmployeeRecord' });

/** Lifetime figures on the employee page (legacy `GetEmployeeDetailAction` stats), every amount in cents. */
export const EmployeeStats = z
  .object({
    ordersProcessed: z.int().nonnegative(),
    totalSales: Money,
    averageTicket: Money,
    /** Closed cards' clock spans, tenths of an hour as legacy shows them ("12.5"). */
    hoursClocked: z.string().regex(/^\d+\.\d$/),
    tipsEarned: Money,
  })
  .meta({ id: 'EmployeeStats' });

/**
 * One row of the employee page's Timecards tab (legacy `GetEmployeeDetailAction` recent_timecards): the 20 newest cards,
 * or while the location flags CA meal premiums every card within 31 days before the newest. Hours and Pay are legacy's
 * arithmetic to the cent (core `legacyDetailCardFigures`): the current wage × the hours, truncated to cents.
 */
export const EmployeeRecentTimecard = z
  .object({
    timecardId: TimecardId,
    businessDate: LocalDate,
    clockedIn: LocationTime,
    clockedOut: LocationTime.nullable(),
    paidBreaks: z.int().nonnegative(),
    unpaidBreaks: z.int().nonnegative(),
    /** As the tab prints it, one decimal ("6.1"). */
    hours: z.string().regex(/^-?\d+\.\d$/),
    pay: Money,
  })
  .meta({ id: 'EmployeeRecentTimecard' });

export const GetEmployee = defineQuery({
  name: 'labor.employees.get',
  summary: "One employee's record at a location: details, job role, wage, permissions, clock status and lifetime figures.",
  input: z.object({ employeeId: EmployeeId, locationId: LocationId }),
  output: z.object({
    employee: EmployeeRecord,
    clockStatus: ClockStatus,
    stats: EmployeeStats,
    recentTimecards: z.array(EmployeeRecentTimecard),
    /**
     * With "Flag potential meal-period premiums" on: the tab's flagged business days over its cards (legacy
     * `premium_days`, core `detailTabMealPremium`) and the cards that trip a violation on their own (legacy
     * `causes_meal_violation`). Null when off.
     */
    mealPremium: z.object({
      days: z.array(TimecardMealPremiumDay.omit({ employeeId: true, outsideRange: true }).meta({ id: 'EmployeeMealPremiumDay' })),
      violations: z.array(TimecardId),
    }).nullable(),
  }),
  permission: 'labor.timecards.manage',
  availability: 'location',
  scope: 'location',
  errors: [EmployeeNotFound],
  agent: { expose: true, surfaces: ['backoffice.assistant', 'crew.manager'] },
  legacy: ['GET /v1/employees/{id}/detail', 'GET /v2/employees/{id}'],
});

/** An employee as the Employees list shows them at one location; a PIN is only ever reported as set or not (and its length). */
export const EmployeeSummary = EmployeeRecord.meta({ id: 'EmployeeSummary' });

/** The employees who work at a location (legacy `GET /v1/employees`), active and deactivated. */
export const ListEmployees = defineQuery({
  name: 'labor.employees.list',
  summary: 'The employees who work at a location with their job role, contact, wage and whether each has a time-clock PIN there.',
  input: z.object({ locationId: LocationId }),
  output: z.object({ employees: z.array(EmployeeSummary) }),
  permission: 'labor.timecards.manage',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant', 'crew.manager'] },
  legacy: ['GET /v1/employees'],
});

export const jobRoleCommands = [UpsertJobRole, DeleteJobRole] as const;
export const peopleQueries = [ListJobRoles, GetEmployee] as const;
